import { useCallback } from 'react'
import { useHistory } from '@fs/zion-router'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import scheduleConfig from '../../../config/schedule'

interface ScheduleStatus {
  showAddedOverlay: () => void
  showPresentOverlay: () => void
  showErrorOverlay: (error?: unknown) => void
}

export default function useScheduleStatus(): ScheduleStatus {
  const history = useHistory()
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const showSavedOverlay = useCallback(
    (alreadyPresent: boolean): void => {
      showStatusOverlay({
        message: alreadyPresent
          ? t('schedule.present.my-schedule.alert', 'Session is in My Schedule')
          : t('schedule.added.my-schedule.alert', 'Added to My Schedule'),
        type: 'success',
        actionLabel: t('schedule.view.my-schedule.alert', 'View Now'),
        actionCallback: () => history.push(scheduleConfig.mySchedulePath),
        transient: true,
      })
    },
    [history, showStatusOverlay, t]
  )
  const showAddedOverlay = useCallback((): void => showSavedOverlay(false), [showSavedOverlay])
  const showPresentOverlay = useCallback((): void => showSavedOverlay(true), [showSavedOverlay])
  const showErrorOverlay = useCallback(
    (error?: unknown): void => {
      // Zion currently exposes plain Error objects for slot limits.
      const isScheduleLimit =
        error instanceof Error &&
        /\b(?:maximum|max|limit)\b/i.test(error.message) &&
        /\bsessions?\b/i.test(error.message) &&
        /\btime\s*slot\b/i.test(error.message)
      showStatusOverlay({
        message: isScheduleLimit
          ? t(
              'schedule.error.my-schedule.alert',
              'You may choose up to four sessions for each time slot. Please select your top four sessions.'
            )
          : t(
              'generic.api.error.message',
              "We're sorry, but there's an issue loading the content. Please try again shortly."
            ),
        type: 'error',
        dismissable: true,
      })
    },
    [showStatusOverlay, t]
  )
  return { showAddedOverlay, showPresentOverlay, showErrorOverlay }
}
